import React, { SVGProps } from 'react';

import { Box, useTheme } from '@mui/material';
import type { BoxProps } from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';

import { ICONS_MAP, IconConfig } from '../../icons';

export type IconName = keyof typeof ICONS_MAP;

export const sizeIconStyles = {
  default: {
    fontSize: '14px',
    padding: '8px',
    lineHeight: '16px',
    size: '36px',
  },
  xSmall: {
    fontSize: '14px',
    padding: '4px',
    lineHeight: '16px',
    size: '28px',
  },
  small: {
    fontSize: '14px',
    padding: '6px',
    lineHeight: '16px',
    size: '32px',
  },
  big: {
    fontSize: '18px',
    padding: '10px',
    lineHeight: '20px',
    size: '44px',
  },
} as const;

export type IconSize = keyof typeof sizeIconStyles;

type InnerIconProps = Partial<SvgIconProps & SVGProps<SVGSVGElement>>;

export type IconProps = Omit<BoxProps, 'color' | 'sx'> & {
  name: IconName;
  sizeVariant?: IconSize;
  color?: string;
  backgroundColor?: string;
  sx?: BoxProps['sx'];
  customSizes?: BoxProps['sx'];
  iconProps?: InnerIconProps;
};

const renderIconContent = (
  config: IconConfig,
  iconProps?: SVGProps<SVGSVGElement>,
  customSizes?: BoxProps['sx']
) => {
  switch (config.type) {
    case 'reactSvg': {
      const { Component: ReactIcon } = config;
      return <ReactIcon {...iconProps} />;
    }
    case 'png': {
      const imgStyle = iconProps?.style as React.CSSProperties | undefined;
      return (
        <img
          src={config.src}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            ...imgStyle,
          }}
        />
      );
    }
    case 'mui': {
      const MUIIcon = config.Component;
      return (
        <MUIIcon sx={{ ...customSizes, fontSize: 'inherit' }} {...iconProps} />
      );
    }
    case 'react': {
      const { Component: ReactIcon, defaultProps } = config;
      return <ReactIcon {...defaultProps} {...iconProps} />;
    }
    default:
      return null;
  }
};

export const Icon: React.FC<IconProps> = ({
  name,
  sizeVariant,
  color,
  backgroundColor,
  sx: userSx,
  iconProps,
  customSizes = { height: 24, width: 24 },
  ...boxProps
}) => {
  const theme = useTheme();
  const config = ICONS_MAP[name];

  if (!config) {
    return null;
  }

  const sizeStyle = sizeVariant ? sizeIconStyles[sizeVariant] : undefined;
  const bg = backgroundColor || (config as any).backgroundColor || undefined;
  const effectiveColor = color ?? (theme as any).text?.primary;
  const sx = { ...customSizes, ...userSx };
  if (!sizeStyle) {
    return (
      <Box
        {...boxProps}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
          ...sx,
        }}
      >
        {renderIconContent(config, iconProps, customSizes)}
      </Box>
    );
  }

  return (
    <Box
      {...boxProps}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: sizeStyle.fontSize,
        height: sizeStyle.size,
        width: sizeStyle.size,
        padding: sizeStyle.padding,
        lineHeight: sizeStyle.lineHeight,
        color: effectiveColor,
        ...(bg && { backgroundColor: bg }),
        '& svg, & img': {
          width: '100%',
          height: '100%',
        },
        ...sx,
      }}
    >
      {renderIconContent(config, iconProps, customSizes)}
    </Box>
  );
};
